/**
 * RetroAgreementStage — workstation right-pane stage for SURFACER/REPORTER
 * cohorts (cortex/drafter cohorts keep PreviewGallery). Image-first retro view
 * of the AI's findings against the signed report:
 *
 *  - a gallery of clean slice tiles (cine, reuses useFindingSlices), one per AI
 *    finding, safety-net first (missed → agreed). The verdict frames the TILE;
 *    the pixels stay clean — no bbox or burned-in label (510(k) constraint the
 *    gallery already follows).
 *  - per-tile report context: the miss phrase (missed) or the report quote
 *    (agreed).
 *  - a right rail: per-case rollup + verdict filter ("missed only" = the
 *    missing-surface lens) + the full signed report with the selected finding's
 *    excerpt highlighted in place (reuses OriginalReportPanel).
 *
 * Verdict palette is monochrome (C2 has no green): missed = signal (the
 * high-salience safety net), agreed = muted. Disagreed/wrong-side has no data
 * source yet (no laterality/attribute compare) and is intentionally absent.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/design-system/components/react";
import {
  adjudicationChip,
  isPredictedNoise,
  useSurfacerCandidates,
} from "../../hooks/useSurfacerCandidates";
import { useReporterComparison } from "../../hooks/useReporterComparisonQueries";
import { useFindingSlices } from "../../hooks/useFindingSlices";
import { pingPongIndex } from "../PreviewGallery";
import { FindingPreviewModal } from "../FindingPreviewModal";
import OriginalReportPanel from "./OriginalReportPanel";
import { formatFindingName } from "../../utils/textFormatters";
import { selectRetroFindings, type RetroFinding, type RetroVerdict } from "./retroFindings";
import type { GalleryFinding } from "../../hooks/useStudyQueries";

const CINE_INTERVAL_MS = 180;

type VerdictFilter = "all" | RetroVerdict;

interface VerdictStyle {
  label: string;
  frame: string; // tile border
  rule: string; // 3px top rule on the image (verdict tint — NOT on the pixels)
  chip: string;
}

const VERDICT_STYLES: Record<RetroVerdict, VerdictStyle> = {
  missed: {
    label: "Missed by report",
    frame: "border-signal-stroke",
    rule: "bg-signal",
    chip: "bg-signal-tint text-signal-text-tint border border-signal-stroke",
  },
  agreed: {
    label: "Agreed",
    frame: "border-border-strong",
    rule: "bg-border-strong",
    chip: "text-fg-muted border border-border-strong",
  },
};

const toGalleryFinding = (f: RetroFinding): GalleryFinding => ({
  condition: f.condition,
  category: f.category,
  topSlice: f.topSlice,
  advancement: f.advancement,
  percentile: f.percentile,
});

// ----------------------------------------------------------------------------

interface TileProps {
  finding: RetroFinding;
  imageUrl: string | null;
  loading: boolean;
  selected: boolean;
  onSelect: () => void;
  onExpand: () => void;
}

const RetroTile: React.FC<TileProps> = ({
  finding,
  imageUrl,
  loading,
  selected,
  onSelect,
  onExpand,
}) => {
  const v = VERDICT_STYLES[finding.verdict];
  // Adjudicator down-rank: greyed tile + a reason chip, mirroring SurfacerPanel.
  const noiseChip = adjudicationChip(finding.predictedVerdict, finding.evidenceQuote);
  const segments = finding.condition.split("/");
  const organ = formatFindingName(segments[0] ?? finding.condition);
  const name = formatFindingName(segments[1] ?? segments[0] ?? finding.condition);
  return (
    <div
      data-testid="retro-tile"
      role="button"
      tabIndex={0}
      aria-label={`Select ${name}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
      className={`group flex cursor-pointer flex-col overflow-hidden rounded-sharp border bg-bg-2 transition-shadow ${v.frame} ${
        selected ? "ring-2 ring-signal" : "hover:ring-2 hover:ring-border"
      } ${noiseChip ? "opacity-60" : ""}`}
    >
      <div className="relative aspect-square bg-black">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${name} preview`}
            className="h-full w-full object-contain"
            draggable={false}
          />
        ) : loading ? (
          <div className="h-full w-full animate-pulse bg-bg-2" aria-hidden="true" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <span className="font-mono text-[10px] text-fg-faint">no preview</span>
          </div>
        )}
        {/* verdict tint lives on a thin rule, never burned into the pixels (510(k)) */}
        <div className={`absolute inset-x-0 top-0 h-[3px] ${v.rule}`} aria-hidden="true" />
        <button
          type="button"
          aria-label={`Expand ${name}`}
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          className="absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-sharp border border-border-strong bg-black/50 text-fg opacity-0 transition-opacity hover:border-fg-dim group-hover:opacity-100"
        >
          ⤢
        </button>
      </div>
      <div className="flex flex-col gap-1.5 p-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono text-[8px] uppercase leading-tight tracking-[0.12em] text-fg-dim">
              {organ}
            </div>
            <div className="truncate text-[13px] leading-snug text-fg">{name}</div>
          </div>
          <div className="flex flex-none flex-col items-end gap-1">
            <span
              className={`rounded-sharp px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider ${v.chip}`}
            >
              {v.label}
            </span>
            {noiseChip && (
              <span
                className="rounded-sharp border border-border px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-fg-faint"
                title={noiseChip.title}
              >
                {noiseChip.label}
              </span>
            )}
          </div>
        </div>
        {finding.advancement != null && (
          <div className="font-mono text-[9px] uppercase tracking-wider text-signal-text-tint">
            OP +{finding.advancement}
            {finding.percentile != null ? ` · ${Math.round(finding.percentile * 100)} pct` : ""}
          </div>
        )}
        <div className="border-t border-border pt-1.5">
          <div
            className={`text-[11px] leading-snug ${
              finding.verdict === "missed" ? "italic text-fg-muted" : "text-fg"
            }`}
          >
            {finding.contextPhrase || "—"}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------

const FilterButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-sharp border px-2 py-1.5 text-center font-mono text-[10px] uppercase tracking-wider transition-colors ${
      active
        ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
        : "border-border-strong text-fg-muted hover:text-fg"
    }`}
  >
    {children}
  </button>
);

const RollupRow: React.FC<{
  label: string;
  count: number;
  max: number;
  verdict: RetroVerdict;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, max, verdict, active, onClick }) => {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full text-left transition-opacity ${active ? "" : "opacity-80 hover:opacity-100"}`}
    >
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-muted">
          {label}
        </span>
        <span
          className={`font-mono text-base font-bold ${verdict === "missed" ? "text-signal-text-tint" : "text-fg"}`}
        >
          {count}
        </span>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded bg-surface-2">
        <div
          className={`h-full rounded ${verdict === "missed" ? "bg-signal" : "bg-border-strong"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </button>
  );
};

interface RailProps {
  missedCount: number;
  agreedCount: number;
  filter: VerdictFilter;
  setFilter: (f: VerdictFilter) => void;
  toggleFilter: (f: RetroVerdict) => void;
  reportText: string;
  selected: RetroFinding | null;
}

const RetroRail: React.FC<RailProps> = ({
  missedCount,
  agreedCount,
  filter,
  setFilter,
  toggleFilter,
  reportText,
  selected,
}) => {
  const max = Math.max(1, missedCount, agreedCount);
  return (
    <div className="flex w-[320px] flex-none flex-col gap-3">
      <div className="rounded-sharp border border-border bg-bg-2 p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-dim">
          Per-case rollup
        </div>
        <div className="mt-3 flex flex-col gap-3">
          <RollupRow
            label="Missed by report"
            count={missedCount}
            max={max}
            verdict="missed"
            active={filter === "missed"}
            onClick={() => toggleFilter("missed")}
          />
          <RollupRow
            label="Agreed"
            count={agreedCount}
            max={max}
            verdict="agreed"
            active={filter === "agreed"}
            onClick={() => toggleFilter("agreed")}
          />
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <FilterButton active={filter === "missed"} onClick={() => setFilter("missed")}>
            Show missed only
          </FilterButton>
          <FilterButton active={filter === "agreed"} onClick={() => setFilter("agreed")}>
            Show agreed
          </FilterButton>
          <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
            Show all
          </FilterButton>
        </div>
        <p className="mt-3 font-mono text-[9px] leading-relaxed tracking-wider text-fg-faint">
          “Missed only” = the missing-surface view.
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-sharp border border-border">
        {selected?.verdict === "missed" && (
          <div className="border-b border-border-strong bg-surface-2 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-signal-text-tint">
            Selected finding not addressed in this report
          </div>
        )}
        <OriginalReportPanel
          reportText={reportText}
          highlightExcerpt={selected?.reportExcerpt ?? null}
        />
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------

interface RetroAgreementStageProps {
  studyId: string;
  deviceId: string;
  /** Reader-row cohort; image job identity may point at a different cohort. */
  cohortId?: string | null;
  meta?: string;
  onOpenReport: () => void;
}

export const RetroAgreementStage: React.FC<RetroAgreementStageProps> = ({
  studyId,
  deviceId,
  cohortId,
  meta,
  onOpenReport,
}) => {
  const [studyUid, jobId] = useMemo(() => {
    const [uid, job] = studyId.split("::");
    return [uid ?? null, job ?? null] as const;
  }, [studyId]);

  const { data: surfacer, isLoading: surfLoading } = useSurfacerCandidates(
    studyUid,
    jobId,
    cohortId,
  );
  const { data: comparison, isLoading: cmpLoading } = useReporterComparison(
    studyUid,
    jobId,
    cohortId,
  );

  const { findings, reportText } = useMemo(
    () => selectRetroFindings(surfacer, comparison),
    [surfacer, comparison],
  );

  // Surfacer-first: open on the missing-surface view (missed only); the rail's
  // Show agreed / Show all toggles reveal the rest.
  const [filter, setFilter] = useState<VerdictFilter>("missed");
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<GalleryFinding | null>(null);
  const [tick, setTick] = useState(0);

  const galleryFindings = useMemo<GalleryFinding[]>(
    () => findings.map(toGalleryFinding),
    [findings],
  );
  const sliceMap = useFindingSlices(studyId, deviceId, galleryFindings);

  const maxFrames = useMemo(
    () => Math.max(1, ...Object.values(sliceMap).map((s) => s.frames.length)),
    [sliceMap],
  );
  useEffect(() => {
    if (maxFrames <= 1) return;
    const id = setInterval(() => setTick((t) => t + 1), CINE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [maxFrames]);

  const visible = useMemo(
    () => (filter === "all" ? findings : findings.filter((f) => f.verdict === filter)),
    [findings, filter],
  );
  // Down-rank in the gallery, mirroring SurfacerPanel: adjudicator-predicted
  // noise (report_mentions / no_report_coverage) sinks below a divider; likely
  // misses (and all agreed) stay on top. Agreed carry a null verdict, so they
  // never sink. `ordered` is the visual order — selection keys off it too.
  const likelyVisible = useMemo(
    () => visible.filter((f) => !isPredictedNoise(f.predictedVerdict)),
    [visible],
  );
  const sunkVisible = useMemo(
    () => visible.filter((f) => isPredictedNoise(f.predictedVerdict)),
    [visible],
  );
  const ordered = useMemo(() => [...likelyVisible, ...sunkVisible], [likelyVisible, sunkVisible]);
  // Selection follows the visible set: filtering to a verdict re-points the rail
  // at a visible finding rather than stranding it on a now-hidden selection. The
  // selectedCondition still wins when it's visible, so unfiltering restores it.
  const selected = useMemo(
    () => ordered.find((f) => f.condition === selectedCondition) ?? ordered[0] ?? null,
    [ordered, selectedCondition],
  );

  // Rollup mirrors the worklist's reporterMissedCount: "Missed by report" is the
  // likely-missed set only — adjudicator-predicted noise (report_mentions /
  // no_report_coverage) sinks below the gallery divider and is discounted here
  // too, so the rollup matches the worklist chip. Agreed carries a null
  // predictedVerdict, so it's never noise; count it independently rather than as
  // the remainder, else the down-ranked findings fold into the agreed tally.
  const missedCount = useMemo(
    () =>
      findings.filter((f) => f.verdict === "missed" && !isPredictedNoise(f.predictedVerdict))
        .length,
    [findings],
  );
  const agreedCount = useMemo(
    () => findings.filter((f) => f.verdict === "agreed").length,
    [findings],
  );

  const toggleFilter = useCallback(
    (f: RetroVerdict) => setFilter((prev) => (prev === f ? "all" : f)),
    [],
  );

  const isLoading = surfLoading || cmpLoading;
  const displayId = studyUid?.slice(-8) ?? studyId;

  const renderTile = (f: RetroFinding) => {
    const state = sliceMap[f.condition];
    const frames = state?.frames ?? [];
    const imageUrl = frames[pingPongIndex(tick, frames.length)] ?? frames[0] ?? null;
    return (
      <RetroTile
        key={`${f.verdict}:${f.condition}`}
        finding={f}
        imageUrl={imageUrl}
        loading={state?.loading ?? true}
        selected={selected?.condition === f.condition}
        onSelect={() => setSelectedCondition(f.condition)}
        onExpand={() => setExpanded(toGalleryFinding(f))}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Header — mirrors PreviewGalleryView: id, meta, disclaimer, Open report */}
      <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-signal-text">{displayId}</span>
        {meta && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-fg-dim">
            {meta}
          </span>
        )}
        <span className="rounded-sharp border border-border px-1.5 py-0.5 font-mono text-[9px] text-fg-faint">
          Informational preview · not for primary diagnosis
        </span>
        <div className="ml-auto">
          <Button size="sm" variant="default" className="rounded-sharp" onClick={onOpenReport}>
            Open report →
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Gallery — image-first */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {visible.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                gap: 14,
              }}
            >
              {likelyVisible.map(renderTile)}
              {sunkVisible.length > 0 && (
                <div
                  style={{ gridColumn: "1 / -1" }}
                  className="flex select-none items-center gap-2 pt-1"
                >
                  <div className="h-px flex-1 bg-border" />
                  <span className="whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-wider text-fg-faint">
                    Likely reported / not covered ({sunkVisible.length})
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              {sunkVisible.map(renderTile)}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-fg-dim">
              {isLoading ? "Loading findings…" : "No surfacer findings for this study"}
            </div>
          )}
        </div>

        <RetroRail
          missedCount={missedCount}
          agreedCount={agreedCount}
          filter={filter}
          setFilter={setFilter}
          toggleFilter={toggleFilter}
          reportText={reportText}
          selected={selected}
        />
      </div>

      {expanded && (
        <FindingPreviewModal
          finding={expanded}
          studyId={studyId}
          deviceId={deviceId}
          onClose={() => setExpanded(null)}
        />
      )}
    </div>
  );
};
